import React, { useState, useRef, useEffect } from 'react';
import Select from 'react-select';
import { FixedSizeList } from 'react-window';
import { useTheme } from '@mui/material/styles';
import apiService from "Network/apiService";
import { API_ENDPOINTS } from 'Network/apiEndpoints';

const ITEM_SIZE = 35; // Height of each item
const MAX_ITEMS = 6; // Maximum number of items to show before scrolling

const CityDropdown = ({ onChange, value, error, helperText }) => {
    const [selectedValue, setSelectedValue] = useState(null);
    const [options, setOptions] = useState([]); // State for storing cities
    const listRef = useRef(null);
    const theme = useTheme();

    // Fetch cities when component mounts
    useEffect(() => {
        const fetchCities = async () => {
            try {
                const response = await apiService.get(API_ENDPOINTS.GET_cities);
                if (response?.success && response?.data) {
                    // Transform the data to match the required format
                    const cityOptions = response.data.map(city => ({
                        value: city.name,
                        label: city.name
                    }));
                    setOptions(cityOptions);
                }
            } catch (error) {
                console.error('Error fetching cities:', error);
                setOptions([]);
            }
        };

        fetchCities();
    }, []);

    useEffect(() => {
        if (value) {
            setSelectedValue({ value, label: value });
        }
    }, [value]);

    const MenuList = React.useCallback(({ children, maxHeight, getValue }) => {
        const [currentValues] = getValue();
        const initialOffset = options.findIndex(
            (opt) => opt.value === currentValues?.value
        ) * ITEM_SIZE;

        const itemCount = children.length || 0;
        const height = Math.min(itemCount * ITEM_SIZE, MAX_ITEMS * ITEM_SIZE);

        return (
            <FixedSizeList
                ref={listRef}
                height={height}
                itemCount={itemCount}
                itemSize={ITEM_SIZE}
                width="100%"
                initialScrollOffset={initialOffset}
            >
                {({ index, style }) => (
                    <div style={style}>{children[index]}</div>
                )}
            </FixedSizeList>
        );
    }, [options]);

    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            borderRadius: theme.shape.borderRadius,
            borderColor: error ? theme.palette.error.main : 
                        (state.isFocused ? theme.palette.primary.main : theme.palette.divider),
            boxShadow: state.isFocused ? `0 0 0 2px ${theme.palette.primary.main}25` : null,
            '&:hover': {
                borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
            },
            minHeight: '40px',
            backgroundColor: theme.palette.background.paper,
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 2,
            backgroundColor: theme.palette.background.paper,
            borderColor: theme.palette.divider,
        }),
        menuList: (provided) => ({
            ...provided,
            padding: 0,
            backgroundColor: theme.palette.background.paper,
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? 
                theme.palette.action.hover : 
                theme.palette.background.paper,
            color: theme.palette.text.primary,
            cursor: 'pointer',
            padding: '8px 12px',
            fontSize: theme.typography.body2.fontSize,
            '&:hover': {
                backgroundColor: theme.palette.action.hover,
            },
        }),
        placeholder: (provided) => ({
            ...provided,
            fontSize: theme.typography.body2.fontSize,
            color: theme.palette.text.secondary,
        }),
        singleValue: (provided) => ({
            ...provided,
            fontSize: theme.typography.body2.fontSize,
            color: theme.palette.text.primary,
        }),
        input: (provided) => ({
            ...provided,
            color: theme.palette.text.primary,
        }),
    };

    const handleChange = (selectedOption) => {
        setSelectedValue(selectedOption);
        onChange(selectedOption?.value || '');
    };

    return (
        <div>
            <Select
                components={{ MenuList }}
                styles={customStyles}
                options={options}
                onChange={handleChange}
                value={selectedValue}
                placeholder="Search for a city..."
                isSearchable
                isClearable
                filterOption={(option, inputValue) => 
                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                }
            />
            {error && (
                <div style={{ 
                    color: theme.palette.error.main, 
                    fontSize: theme.typography.caption.fontSize,
                    marginTop: theme.spacing(0.5)
                }}>
                    {helperText}
                </div>
            )}
        </div>
    );
};

export default React.memo(CityDropdown);


